// Reusable bulk-importer wizard — upload → map columns → preview → confirm → progress.
// Works with the /api/import/* backend (products/contacts/assets/staff/consumables/vendors)
// plus the /api/import/quicksell/* native path when the file is a QuickSell export.
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Loader2, ArrowRight, Check, AlertTriangle, FileSpreadsheet } from "lucide-react";

const KIND_LABELS = {
  products: "Products / Catalog",
  contacts: "Contacts / Customers",
  assets: "Factory Assets",
  staff: "Staff / HR",
  consumables: "Consumables",
  vendors: "Vendors",
};

// Fields the user must map for each kind (mirrors backend hints).
const KIND_REQUIRED_FIELDS = {
  products: ["design_code", "weight_grams", "category", "sub_category", "metal", "purity", "price", "description", "image_filename"],
  contacts: ["title", "name", "company_name", "mobile", "email", "contact_type", "city", "state", "address", "gst"],
  assets: ["name", "category", "department", "serial_number", "vendor", "purchase_price", "useful_life_years", "salvage_value", "status", "location", "notes"],
  staff: ["name", "email", "mobile", "role", "department", "shift", "pay_type", "salary", "rate", "status", "hr_external_id"],
  consumables: ["name", "sku", "unit", "category", "default_price", "stock_qty", "reorder_level", "vendor"],
  vendors: ["name", "gst", "mobile", "email", "address", "city", "category"],
};

export default function BulkImporter({ kind = "products", onDone, quicksellFirst = false }) {
  const [step, setStep] = useState("upload"); // upload | map | progress
  const [file, setFile] = useState(null);
  const [upload, setUpload] = useState(null);   // upload_id + preview
  const [columnMap, setColumnMap] = useState({});
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [mode, setMode] = useState(quicksellFirst ? "quicksell" : "generic");
  const [weightSource, setWeightSource] = useState("auto");
  const [fetchImages, setFetchImages] = useState(true);
  const pollRef = useRef();

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const uploadFile = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      let endpoint;
      if (kind === "products" && mode === "quicksell") {
        endpoint = "/import/quicksell/upload";
      } else {
        endpoint = "/import/upload";
        fd.append("kind", kind);
      }
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUpload(data);
      if (mode === "quicksell") {
        setWeightSource(data.suggested_weight_source || "auto");
        setStep("map"); // QS has no user column-map, jumps to confirm step
      } else {
        setColumnMap(data.suggested_map || {});
        setStep("map");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const startImport = async () => {
    setStarting(true);
    try {
      let jobRes;
      if (mode === "quicksell") {
        jobRes = await api.post("/import/quicksell/start", {
          upload_id: upload.upload_id,
          filename: upload.filename,
          weight_source: weightSource,
          fetch_images: fetchImages,
          image_concurrency: 3,
        });
      } else {
        jobRes = await api.post("/import/start", {
          upload_id: upload.upload_id,
          filename: upload.filename,
          kind,
          column_map: columnMap,
          options: {},
        });
      }
      setJobId(jobRes.data.job_id);
      setStep("progress");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start");
    } finally { setStarting(false); }
  };

  // Poll job every 700ms while active
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/import/jobs/${jobId}`);
        if (cancelled) return;
        setJob(data);
        if (["completed", "failed", "partial"].includes(data.status) && !data.images_total) {
          return; // stop polling
        }
        if (["completed", "partial"].includes(data.status) && data.images_total && data.images_done >= data.images_total) {
          return; // images also finished
        }
        pollRef.current = setTimeout(tick, 700);
      } catch {
        pollRef.current = setTimeout(tick, 1500);
      }
    };
    tick();
    return () => { cancelled = true; if (pollRef.current) clearTimeout(pollRef.current); };
  }, [jobId]);

  // --- render ---
  return (
    <div className="space-y-4" data-testid={`bulk-importer-${kind}`}>
      {/* Step tracker */}
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#4A5A50]">
        <StepPill active={step === "upload"}>1 · Upload</StepPill>
        <ArrowRight size={12}/>
        <StepPill active={step === "map"}>2 · {mode === "quicksell" ? "Confirm" : "Map columns"}</StepPill>
        <ArrowRight size={12}/>
        <StepPill active={step === "progress"}>3 · Process</StepPill>
      </div>

      {step === "upload" && (
        <Card className="border-[#E4DFCF] rounded-2xl"><CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium"><FileSpreadsheet size={16} className="text-[#0F5E39]"/> {KIND_LABELS[kind] || kind}</div>
          {kind === "products" && (
            <div className="flex gap-2 text-xs">
              <button onClick={() => setMode("quicksell")} className={`px-3 py-1 rounded-full border ${mode === "quicksell" ? "border-[#0F5E39] bg-[#E1F0E6] text-[#0F5E39]" : "border-[#E4DFCF] text-[#4A5A50]"}`} data-testid="mode-quicksell">QuickSell export</button>
              <button onClick={() => setMode("generic")} className={`px-3 py-1 rounded-full border ${mode === "generic" ? "border-[#0F5E39] bg-[#E1F0E6] text-[#0F5E39]" : "border-[#E4DFCF] text-[#4A5A50]"}`} data-testid="mode-generic">Generic CSV / Excel</button>
            </div>
          )}
          <div>
            <Label>File (.csv or .xlsx)</Label>
            <Input type="file" accept=".csv,.xlsx,.xlsm" onChange={onPick} data-testid="import-file-input"/>
            {file && <div className="text-xs text-[#4A5A50] mt-1">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
          </div>
          <Button onClick={uploadFile} disabled={!file || uploading} className="bg-[#0F5E39] hover:bg-[#083F22] text-white rounded-full h-11" data-testid="import-upload-btn">
            {uploading ? <Loader2 size={14} className="animate-spin mr-1"/> : <Upload size={14} className="mr-1"/>} Upload &amp; preview
          </Button>
        </CardContent></Card>
      )}

      {step === "map" && upload && mode === "quicksell" && (
        <Card className="border-[#E4DFCF] rounded-2xl"><CardContent className="p-5 space-y-4">
          <div className="text-sm">
            <div><b>{upload.grouped_designs}</b> designs from <b>{upload.total_rows}</b> rows.</div>
            {!upload.recognised && <div className="text-amber-700 mt-1"><AlertTriangle size={12} className="inline"/> QuickSell headers not detected — switch to Generic if this isn't a QuickSell file.</div>}
          </div>
          <div>
            <Label>Weight column</Label>
            <Select value={weightSource} onValueChange={setWeightSource}>
              <SelectTrigger data-testid="qs-weight-source"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto (recommended)</SelectItem>
                <SelectItem value="weight">"Weight" column</SelectItem>
                <SelectItem value="product_price">"Product Price" column (typical when Weight is blank)</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-[#4A5A50] mt-1">Suggested: <b>{upload.suggested_weight_source}</b></div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={fetchImages} onChange={(e) => setFetchImages(e.target.checked)} data-testid="qs-fetch-images"/>
            Download product images from Product Picture URL (throttled, background)
          </label>
          {upload.preview_designs?.length > 0 && (
            <div className="border border-[#E4DFCF] rounded-lg p-3 text-xs">
              <div className="font-medium mb-2">Preview (first {upload.preview_designs.length})</div>
              <ul className="space-y-1">
                {upload.preview_designs.map(p => (
                  <li key={p.design_code}><b>{p.design_code}</b> — {p.name} ({p.variant_count} variants, weights: {p.sample_weights.join(", ")})</li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setStep("upload"); setUpload(null); }}>Back</Button>
            <Button onClick={startImport} disabled={starting} className="bg-[#0F5E39] hover:bg-[#083F22] text-white rounded-full" data-testid="qs-start-btn">
              {starting ? <Loader2 size={14} className="animate-spin mr-1"/> : <Check size={14} className="mr-1"/>} Start import
            </Button>
          </div>
        </CardContent></Card>
      )}

      {step === "map" && upload && mode !== "quicksell" && (
        <Card className="border-[#E4DFCF] rounded-2xl"><CardContent className="p-5 space-y-4">
          <div className="text-sm text-[#4A5A50]">
            <b>{upload.total_rows}</b> rows detected · {upload.headers.length} columns.
            Map each app field to a spreadsheet column.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[50vh] overflow-y-auto pr-1">
            {(KIND_REQUIRED_FIELDS[kind] || []).map(field => (
              <div key={field}>
                <Label className="capitalize">{field.replace(/_/g, " ")}</Label>
                <Select value={columnMap[field] || "_none"} onValueChange={(v) => setColumnMap({ ...columnMap, [field]: v === "_none" ? "" : v })}>
                  <SelectTrigger data-testid={`map-${field}`}><SelectValue placeholder="— skip —"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— skip —</SelectItem>
                    {upload.headers.filter(Boolean).map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          {upload.preview?.length > 0 && (
            <details className="border border-[#E4DFCF] rounded-lg p-3">
              <summary className="text-sm cursor-pointer">Preview first {upload.preview.length} rows</summary>
              <div className="mt-3 overflow-x-auto text-xs">
                <table className="w-full"><thead><tr className="text-[#4A5A50] uppercase">
                  {upload.headers.slice(0, 8).map(h => <th key={h} className="text-left p-1 pr-3">{h}</th>)}
                </tr></thead><tbody>
                  {upload.preview.slice(0, 5).map((r, i) => (
                    <tr key={i} className="border-t border-[#E4DFCF]">
                      {upload.headers.slice(0, 8).map(h => <td key={h} className="p-1 pr-3 truncate max-w-[120px]">{String(r[h] ?? "")}</td>)}
                    </tr>
                  ))}
                </tbody></table>
              </div>
            </details>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setStep("upload"); setUpload(null); }}>Back</Button>
            <Button onClick={startImport} disabled={starting} className="bg-[#0F5E39] hover:bg-[#083F22] text-white rounded-full" data-testid="import-start-btn">
              {starting ? <Loader2 size={14} className="animate-spin mr-1"/> : <Check size={14} className="mr-1"/>} Start import
            </Button>
          </div>
        </CardContent></Card>
      )}

      {step === "progress" && job && (
        <Card className="border-[#E4DFCF] rounded-2xl"><CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 text-sm">
            {job.status === "completed" && <Check size={16} className="text-[#0F5E39]"/>}
            {job.status === "processing" && <Loader2 size={16} className="animate-spin text-[#0F5E39]"/>}
            {job.status === "partial" && <AlertTriangle size={16} className="text-amber-600"/>}
            {job.status === "failed" && <AlertTriangle size={16} className="text-red-600"/>}
            <span className="font-medium capitalize">{job.status}</span>
            <span className="text-[#4A5A50]">· {job.processed || 0} / {job.total_rows || 0} rows</span>
          </div>
          <div className="h-2 bg-[#F1EDDF] rounded-full overflow-hidden">
            <div className="h-full bg-[#0F5E39] transition-all" style={{ width: job.total_rows ? `${Math.round((job.processed / job.total_rows) * 100)}%` : "0%" }}/>
          </div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <MiniStat label="Inserted" value={job.inserted || 0} tone="ok"/>
            <MiniStat label="Updated" value={job.updated || 0} tone="ok"/>
            <MiniStat label="Skipped" value={job.skipped || 0} tone={job.skipped ? "warn" : ""}/>
          </div>
          {typeof job.images_total === "number" && job.images_total > 0 && (
            <div>
              <div className="text-xs text-[#4A5A50] mb-1">Downloading images · {job.images_done || 0} / {job.images_total}</div>
              <div className="h-2 bg-[#F1EDDF] rounded-full overflow-hidden">
                <div className="h-full bg-[#C9A24B] transition-all" style={{ width: `${Math.round(((job.images_done || 0) / job.images_total) * 100)}%` }}/>
              </div>
            </div>
          )}
          {job.errors?.length > 0 && (
            <details className="border border-red-200 bg-red-50/40 rounded-lg p-3">
              <summary className="text-sm cursor-pointer text-red-700">{job.errors.length} row error(s)</summary>
              <ul className="mt-2 text-xs space-y-1 max-h-40 overflow-y-auto">
                {job.errors.slice(0, 50).map((e, i) => <li key={i}>Row {e.row}: {e.message}</li>)}
              </ul>
            </details>
          )}
          {["completed", "partial", "failed"].includes(job.status) && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setJobId(null); setJob(null); setUpload(null); setFile(null); setStep("upload"); }}>Import another</Button>
              {onDone && <Button onClick={onDone} className="bg-[#0F5E39] hover:bg-[#083F22] text-white rounded-full" data-testid="import-done-btn">Done</Button>}
            </div>
          )}
        </CardContent></Card>
      )}
    </div>
  );
}

function StepPill({ active, children }) {
  return <span className={`px-2 py-1 rounded-full border ${active ? "border-[#0F5E39] bg-[#E1F0E6] text-[#0F5E39]" : "border-[#E4DFCF] text-[#4A5A50]"}`}>{children}</span>;
}

function MiniStat({ label, value, tone }) {
  const c = tone === "ok" ? "text-[#0F5E39]" : tone === "warn" ? "text-amber-700" : "text-[#0F1E15]";
  return (
    <div className="border border-[#E4DFCF] rounded-lg px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">{label}</div>
      <div className={`font-serif text-xl mt-0.5 ${c}`}>{value}</div>
    </div>
  );
}
